import type { Ref } from "vue";
import { computed } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import moment from "moment";
import type { AppointmentInput, UpdateReceiptInput } from "@phurshell/psiapp-frontend-core/domain";
import { getAppointments } from "@/di/useCases";
import { appointmentRepo } from "@/di/repositories";

export function useAppointmentsList(date: Ref<string>) {
  const monthRange = computed(() => ({
    date_start: moment(date.value).startOf("month").format("YYYY-MM-DD"),
    date_end: moment(date.value).endOf("month").format("YYYY-MM-DD"),
  }));

  const { data, isPending } = useQuery({
    queryKey: ["appointments", "list", monthRange],
    queryFn: () => getAppointments.execute(monthRange.value),
  });

  const allAppointments = computed(() => data.value ?? []);

  const appointments = computed(() =>
    allAppointments.value.filter((e) => e.date.split(" ")[0] === date.value),
  );

  return { allAppointments, appointments, isPending };
}

export function useInvalidateAppointments() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["appointments"] });
}

export function useAppointmentDetail(id: Ref<number | undefined>) {
  return useQuery({
    queryKey: ["appointments", "detail", id],
    queryFn: () => appointmentRepo.getAppointmentById(id.value!),
    enabled: computed(() => !!id.value),
  });
}

export function useCreateRescheduleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { appointment_id: number; new_date: string }) =>
      appointmentRepo.createRescheduleRequest(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["appointments"] }),
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...params }: { id: number } & AppointmentInput) =>
      appointmentRepo.updateAppointment(id, params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["appointments"] }),
  });
}

export function useUpdateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...params }: { id: number } & UpdateReceiptInput) =>
      appointmentRepo.updateReceipt(id, params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["appointments"] }),
  });
}

export function useUpdateRescheduleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      appointmentRepo.updateRescheduleRequest(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["appointments"] }),
  });
}

export function useAppointmentRecurrency(id: Ref<number | undefined>) {
  return useQuery({
    queryKey: ["appointments", "recurrency", id],
    queryFn: () => appointmentRepo.getRecurrencyById(id.value!),
    enabled: computed(() => !!id.value),
  });
}

export function useCancelRecurrency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => appointmentRepo.cancelRecurrency(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["appointments", "recurrency"] }),
  });
}
